import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/api/client";
import type { NotificationCategory, NotificationSettings } from "@/api/types";
import { useAuth } from "@/stores/auth";
import type { InboxTab } from "./meta";

export const notificationKeys = {
  all: ["notifications"] as const,
  list: (unread: boolean, tab?: string) => ["notifications", "list", unread, tab ?? ""] as const,
  unread: ["notifications", "unread"] as const,
  prefs: ["notifications", "prefs"] as const,
  settings: ["notifications", "settings"] as const,
  devices: ["notifications", "devices"] as const,
};

export function useUnreadNotifications() {
  const authed = useAuth((s) => s.status === "authenticated");
  return useQuery({
    queryKey: notificationKeys.unread,
    queryFn: async () => (await unwrap(api.GET("/api/v1/notifications/unread-count/"))).unread,
    enabled: authed,
    staleTime: 30_000,
    // Poll while the app is visible (WebSockets may be off on the API host).
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useNotifications(unread = false, tab: InboxTab = "all") {
  return useInfiniteQuery({
    queryKey: notificationKeys.list(unread, tab),
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/api/v1/notifications/", {
          params: { query: { unread: unread || undefined, type: tab === "all" ? undefined : tab, page: pageParam } },
        }),
      ),
    initialPageParam: 1,
    getNextPageParam: (last, pages) => (last.next ? pages.length + 1 : undefined),
    placeholderData: keepPreviousData,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids?: string[]) => {
      if (!ids) return (await unwrap(api.POST("/api/v1/notifications/read-all/"))).unread;
      const res = await unwrap(api.POST("/api/v1/me/notifications/read/", { body: { ids } }));
      return res.unread;
    },
    onSuccess: (unread) => {
      qc.setQueryData(notificationKeys.unread, unread);
      void qc.invalidateQueries({ queryKey: [...notificationKeys.all, "list"] });
    },
  });
}

export function useNotificationClicked() {
  return useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/api/v1/notifications/{id}/clicked/", { params: { path: { id } } })),
  });
}

export function usePreferences() {
  return useQuery({
    queryKey: notificationKeys.prefs,
    queryFn: () => unwrap(api.GET("/api/v1/notifications/preferences/")),
  });
}

export function useSetPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { category: NotificationCategory; channels: Partial<Record<"in_app" | "email" | "sms" | "push", boolean>> }) =>
      unwrap(api.PUT("/api/v1/notifications/preferences/", { body: { preferences: [body] } })),
    onSuccess: (rows) => qc.setQueryData(notificationKeys.prefs, rows),
  });
}

export function useNotificationSettings(enabled = true) {
  const authed = useAuth((s) => s.status === "authenticated");
  return useQuery({
    queryKey: notificationKeys.settings,
    queryFn: () => unwrap(api.GET("/api/v1/notifications/settings/")),
    enabled: authed && enabled,
    staleTime: 5 * 60_000,
  });
}

export function useUpdateNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<Omit<NotificationSettings, "push_prompt_snoozed_until">>) =>
      unwrap(api.PATCH("/api/v1/notifications/settings/", { body })),
    onSuccess: (data) => qc.setQueryData(notificationKeys.settings, data),
  });
}

export function useSnoozePrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST("/api/v1/notifications/prompt-snooze/")),
    onSuccess: (data) => qc.setQueryData(notificationKeys.settings, data),
  });
}

export function usePushDevices() {
  return useQuery({
    queryKey: notificationKeys.devices,
    queryFn: () => unwrap(api.GET("/api/v1/notifications/push-subscriptions/")),
  });
}

export function useRemoveDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/api/v1/notifications/push-subscriptions/{id}/", { params: { path: { id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: notificationKeys.devices }),
  });
}

export function useSendTestPush() {
  return useMutation({ mutationFn: () => unwrap(api.POST("/api/v1/notifications/push-test/")) });
}
